import * as cookie from '@tt/cookie';

export const SIGNED_OUT_EVENT = 'app:signed-out';
export const signedOutStorageKey = (environment) => `tt:session-signed-out:${environment}`;
const pending = new Map();

export const logoutProfile = (
    hostname = typeof window === 'undefined' ? '' : window.location.hostname
) => {
    const host = hostname.toLowerCase().replace(/\.$/, '');
    const staging =
        host.includes('demo') || host.includes('staging') || /(?:tt-stg|[.-]stg)\./.test(host);
    const dev =
        ['localhost', '127.0.0.1', '[::1]', '::1'].includes(host) ||
        /(?:tt-dev|[.-]dev)\./.test(host);
    const environment = staging ? 'stg' : dev ? 'dev' : 'prod';
    const ttOrigin =
        environment === 'stg'
            ? 'https://tt-stg.tradingterminal.com'
            : environment === 'dev'
              ? 'https://tt-dev.tradingterminal.com'
              : 'https://tradingterminal.com';
    const coreOrigin =
        environment === 'stg'
            ? 'https://core-stg.tradingterminal.com'
            : environment === 'dev'
              ? 'https://core-dev.tradingterminal.com'
              : 'https://core.tradingterminal.com';
    return { environment, ttOrigin, coreOrigin, loginUrl: `${ttOrigin}/auth/login` };
};

const readToken = (profile) => cookie.parseUserToken([profile.ttOrigin]);
const failed = () => new Error('Could not sign out of Trading Terminal. Please try again.');

async function revoke(options, profile) {
    const sourceCookieToken = readToken(profile);
    let token = sourceCookieToken || options.token || '';
    let kind = token ? 'signed-out' : 'already-signed-out';
    if (token) {
        const request = options.request || fetch;
        let acknowledged = false;
        for (let attempt = 0; attempt < 2; attempt += 1) {
            const response = await request(`${profile.coreOrigin}/v1/auth/logout`, {
                method: 'POST',
                credentials: 'include',
                cache: 'no-store',
                redirect: 'error',
                headers: { Accept: 'application/json', token },
                signal: options.signal || AbortSignal.timeout(12000)
            });
            const data = await response.json().catch(() => null);
            if (
                response.status === 200 &&
                data?.code === 'TOKEN_UPGRADED' &&
                attempt === 0 &&
                typeof data.token === 'string' &&
                data.token
            ) {
                token = data.token;
                let user = null;
                try {
                    user = JSON.parse(cookie.getCookie('user', [profile.ttOrigin]));
                    if (typeof user === 'string') user = JSON.parse(user);
                } catch {
                    /* A supplied store token can exist without a cookie. */
                }
                if (user && typeof user === 'object')
                    cookie.setCookie('user', { ...user, token }, undefined, [profile.ttOrigin]);
                if (options.onTokenUpgrade) await options.onTokenUpgrade(token);
                continue;
            }
            if (response.status === 200 && data?.ok === true && data?.sessionRevoked === true)
                acknowledged = true;
            else if (response.status === 401 && data?.code === 'SESSION_REVOKED') {
                acknowledged = true;
                kind = 'already-signed-out';
            }
            break;
        }
        if (!acknowledged) throw failed();
    }
    // A new login that won while the old session was being revoked must survive.
    const latest = readToken(profile);
    if (latest && latest !== sourceCookieToken && latest !== token)
        throw new Error('Your session changed. Please try signing out again.');
    cookie.clearEnvAuthCookies([profile.ttOrigin]);
    if (options.onCleanup) await options.onCleanup();
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(SIGNED_OUT_EVENT));
        try {
            window.localStorage.setItem(
                signedOutStorageKey(profile.environment),
                JSON.stringify({ environment: profile.environment, at: Date.now() })
            );
        } catch {
            /* Core revocation coordinates other origins even when browser storage is blocked. */
        }
    }
    return { kind, loginUrl: profile.loginUrl };
}

/** Explicit user intent only. Cookie removal and forced cleanup never call this. */
export function globalLogout(options = {}) {
    const profile = logoutProfile(options.hostname);
    if (pending.has(profile.environment)) return pending.get(profile.environment);
    const task = revoke(options, profile).finally(() => pending.delete(profile.environment));
    pending.set(profile.environment, task);
    return task;
}
